import { useMemo } from "react";
import _ from "lodash";
import { useQuery } from "@apollo/client";
import {
  getGradeDetailsBatchQuery,
  getCourseDetailsBatchQuery,
} from "../modules/ParticipantsQueries";
import { useI18n } from "Hooks";
import useReduxState from "Hooks/useReduxState";
import {
  TAB_TYPES,
  useParticipantsModal,
} from "../context/ParticipantsModalContext";
import {
  getDefaultStudentNameOrderPreference,
  getOrganizationId,
} from "modules/CommonSelectors";
import useParticipantsFeed from "./useParticipantsFeed";
import { normaliseParticipant } from "../utils/normaliseParticipant";
import useGradeBatchDetails from "./useGradeBatchDetails";
import useCourseBatchDetails from "./useCourseBatchDetails";
import useCustomParticipantsBatch from "./useCustomParticipantsBatch";
import useUserBatchDetails from "./useUserBatchDetails";
import { isNewParticipantSelectorEnabled } from "MyCalendar/modules/Utils";
import { isBigOrgFFEnabledAndStaffUser } from "../../../modules/Utils";

/**
 * Orchestrates participant data for all tabs:
 * - GRADES: virtualized, batch-fetched via useGradeBatchDetails
 * - COURSES: virtualized, batch-fetched via useCourseBatchDetails
 * - STUDENTS / STAFF / FAMILY: fetched from API via useParticipantsFeed
 *   (or client-side normalised via useCustomParticipantsBatch for custom lists)
 *
 * @returns {{ isLoading: boolean, isFetchingMore: boolean, data: { listData: object, totalCount: number }, hasNextPage: boolean, loadMore: Function }}
 */
const useParticipantsData = () => {
  const { t } = useI18n(["common"]);
  const {
    activeTab,
    searchText,
    associatedEntities = [],
    isCustomParticipantList,
    eventType,
  } = useParticipantsModal();
  const organizationId = getOrganizationId();

  const defaultStudentNameOrderPreference = useReduxState(
    getDefaultStudentNameOrderPreference
  );

  const gradeIds = useMemo(
    () =>
      _.map(
        _.filter(
          associatedEntities,
          item => _.get(item, "__typename") === "Grade"
        ),
        "id"
      ),
    [associatedEntities]
  );

  const courseIds = useMemo(
    () =>
      _.map(
        _.filter(
          associatedEntities,
          item => _.get(item, "__typename") === "Course"
        ),
        "id"
      ),
    [associatedEntities]
  );

  const {
    participants,
    totalCount: feedTotalCount,
    isLoading,
    isFetchingMore,
    hasNextPage,
    loadMore,
  } = useParticipantsFeed({
    organizationId,
    activeTab,
    searchText,
    gradeIds,
    courseIds,
    skip: isCustomParticipantList,
  });

  const isLazyLoadEnabled =
    isNewParticipantSelectorEnabled() && isBigOrgFFEnabledAndStaffUser();

  const {
    onRangeChanged: onGradeRangeChanged,
    detailsMap: gradeDetailsMap,
    searchResults: gradeSearchResults,
    isSearchLoading: isGradeSearchLoading,
  } = useGradeBatchDetails({
    gradeIds,
    searchText,
    skip: !isLazyLoadEnabled || activeTab !== TAB_TYPES.GRADES,
  });

  const {
    onRangeChanged: onCourseRangeChanged,
    detailsMap: courseDetailsMap,
    searchResults: courseSearchResults,
    isSearchLoading: isCourseSearchLoading,
  } = useCourseBatchDetails({
    courseIds,
    searchText,
    skip: !isLazyLoadEnabled || activeTab !== TAB_TYPES.COURSES,
  });

  // When isLazyLoadEnabled is false, grade and course details (name/title) are
  // already in associatedEntities. Fetch counts separately via a single batch
  // query per type so the row UI can show staffCount/studentCount/familyMemberCount.
  const { data: gradeCountsData, loading: isGradeCountsLoading } = useQuery(
    getGradeDetailsBatchQuery,
    {
      variables: { ids: gradeIds },
      skip: isLazyLoadEnabled || _.isEmpty(gradeIds),
      fetchPolicy: "cache-and-network",
    }
  );

  const { data: courseCountsData, loading: isCourseCountsLoading } = useQuery(
    getCourseDetailsBatchQuery,
    {
      variables: { ids: courseIds },
      skip: isLazyLoadEnabled || _.isEmpty(courseIds),
      fetchPolicy: "cache-and-network",
    }
  );

  // Build detailsMaps from associatedEntities and merge in counts from the
  // batch query responses once they arrive.
  const gradeDetailsMapFromEntities = useMemo(() => {
    if (isLazyLoadEnabled) return {};
    const countsById = _.keyBy(_.get(gradeCountsData, "multiNode", []), "id");
    return _.keyBy(
      _.map(
        _.filter(associatedEntities, item => item.__typename === "Grade"),
        ({ id, name, curriculumProgram }) => ({
          id,
          name,
          curriculumProgram,
          staffCount: _.get(countsById, [id, "staffCount"]),
          studentCount: _.get(countsById, [id, "studentCount"]),
          familyMemberCount: _.get(countsById, [id, "familyMemberCount"]),
        })
      ),
      "id"
    );
  }, [isLazyLoadEnabled, associatedEntities, gradeCountsData]);

  const courseDetailsMapFromEntities = useMemo(() => {
    if (isLazyLoadEnabled) return {};
    const countsById = _.keyBy(_.get(courseCountsData, "multiNode", []), "id");
    return _.keyBy(
      _.map(
        _.filter(associatedEntities, item => item.__typename === "Course"),
        ({ id, title, grades }) => ({
          id,
          title,
          grades,
          studentCount: _.get(countsById, [id, "students", "totalCount"]),
          staffCount: _.get(countsById, [id, "collaborators", "totalCount"]),
          familyMemberCount: _.get(countsById, [
            id,
            "familyMembers",
            "totalCount",
          ]),
        })
      ),
      "id"
    );
  }, [isLazyLoadEnabled, associatedEntities, courseCountsData]);

  // When !isLazyLoadEnabled, all grade/course data is already in memory —
  // filter locally by name/title instead of firing a network search query.
  const gradeLocalSearchResults = useMemo(() => {
    if (isLazyLoadEnabled) return null;
    const trimmed = _.toLower(_.trim(searchText));
    if (!trimmed) return null;
    return _.filter(gradeIds, id =>
      _.includes(
        _.toLower(_.get(gradeDetailsMapFromEntities, [id, "name"], "")),
        trimmed
      )
    );
  }, [isLazyLoadEnabled, searchText, gradeIds, gradeDetailsMapFromEntities]);

  const courseLocalSearchResults = useMemo(() => {
    if (isLazyLoadEnabled) return null;
    const trimmed = _.toLower(_.trim(searchText));
    if (!trimmed) return null;
    return _.filter(courseIds, id =>
      _.includes(
        _.toLower(_.get(courseDetailsMapFromEntities, [id, "title"], "")),
        trimmed
      )
    );
  }, [isLazyLoadEnabled, searchText, courseIds, courseDetailsMapFromEntities]);

  const {
    onRangeChanged: onCustomRangeChanged,
    detailsMap: customDetailsMap,
    filteredActiveTabIds: customFilteredActiveTabIds,
    totalCount: customTotalCount,
    isLoading: isCustomLoading,
  } = useCustomParticipantsBatch({
    associatedEntities:
      isCustomParticipantList && !isLazyLoadEnabled ? associatedEntities : [],
    activeTab,
    searchText,
    organizationId,
  });

  const {
    onRangeChanged: onUserRangeChanged,
    detailsMap: userDetailsMap,
    filteredActiveTabIds: userFilteredActiveTabIds,
    totalCount: userTotalCount,
  } = useUserBatchDetails({
    associatedEntities:
      isCustomParticipantList && isLazyLoadEnabled ? associatedEntities : [],
    activeTab,
    searchText,
    skip: !isCustomParticipantList || !isLazyLoadEnabled,
    eventType,
  });

  // Route to the correct hook output based on the feature flag
  const finalCustomDetailsMap = isLazyLoadEnabled
    ? userDetailsMap
    : customDetailsMap;
  const filteredActiveTabIds = isLazyLoadEnabled
    ? userFilteredActiveTabIds
    : customFilteredActiveTabIds;
  const finalCustomTotalCount = isLazyLoadEnabled
    ? userTotalCount
    : customTotalCount;
  const finalOnCustomRangeChanged = isLazyLoadEnabled
    ? onUserRangeChanged
    : onCustomRangeChanged;

  const normalisedParticipants = useMemo(
    () =>
      _.map(participants, participant =>
        normaliseParticipant(participant, defaultStudentNameOrderPreference, t)
      ),
    [participants, defaultStudentNameOrderPreference, t]
  );

  // Derive per-type normalised lists for custom participant listData.
  // Only computed when isCustomParticipantList is true; entities already carry
  // all fields needed by normaliseParticipant so no network request is required.
  const customNormalisedByType = useMemo(() => {
    if (!isCustomParticipantList || isLazyLoadEnabled) {
      return { students: [], staff: [], family: [] };
    }
    const grouped = _.groupBy(associatedEntities, "__typename");
    return {
      students: _.map(_.get(grouped, "Student", []), p =>
        normaliseParticipant(p, defaultStudentNameOrderPreference, t)
      ),
      staff: _.map(_.get(grouped, "Staff", []), p =>
        normaliseParticipant(p, defaultStudentNameOrderPreference, t)
      ),
      family: _.map(_.get(grouped, "FamilyMember", []), p =>
        normaliseParticipant(p, defaultStudentNameOrderPreference, t)
      ),
    };
  }, [
    isCustomParticipantList,
    isLazyLoadEnabled,
    associatedEntities,
    defaultStudentNameOrderPreference,
    t,
  ]);

  const isStaticTab =
    activeTab === TAB_TYPES.GRADES || activeTab === TAB_TYPES.COURSES;

  const isSearchLoading =
    (activeTab === TAB_TYPES.GRADES && isGradeSearchLoading) ||
    (activeTab === TAB_TYPES.COURSES && isCourseSearchLoading) ||
    (!isStaticTab &&
      !isCustomParticipantList &&
      isLoading &&
      !_.isEmpty(_.trim(searchText)));

  const listData = useMemo(() => {
    if (isCustomParticipantList) {
      const lowerSearch = _.toLower(_.trim(searchText));
      const filterBySearch = items => {
        if (_.isEmpty(lowerSearch)) return items;
        return _.filter(items, item =>
          _.includes(_.toLower(item.name), lowerSearch)
        );
      };
      return {
        [TAB_TYPES.GRADES]: [],
        [TAB_TYPES.COURSES]: [],
        [TAB_TYPES.STUDENTS]:
          activeTab === TAB_TYPES.STUDENTS
            ? filterBySearch(customNormalisedByType.students)
            : customNormalisedByType.students,
        [TAB_TYPES.FAMILY]:
          activeTab === TAB_TYPES.FAMILY
            ? filterBySearch(customNormalisedByType.family)
            : customNormalisedByType.family,
        [TAB_TYPES.STAFF]:
          activeTab === TAB_TYPES.STAFF
            ? filterBySearch(customNormalisedByType.staff)
            : customNormalisedByType.staff,
      };
    }
    return {
      [TAB_TYPES.GRADES]: gradeIds,
      [TAB_TYPES.COURSES]: courseIds,
      [TAB_TYPES.STUDENTS]:
        activeTab === TAB_TYPES.STUDENTS ? normalisedParticipants : [],
      [TAB_TYPES.FAMILY]:
        activeTab === TAB_TYPES.FAMILY ? normalisedParticipants : [],
      [TAB_TYPES.STAFF]:
        activeTab === TAB_TYPES.STAFF ? normalisedParticipants : [],
    };
  }, [
    isCustomParticipantList,
    gradeIds,
    courseIds,
    normalisedParticipants,
    activeTab,
    customNormalisedByType,
    searchText,
  ]);

  const staticTabCount =
    activeTab === TAB_TYPES.GRADES ? gradeIds.length : courseIds.length;
  const totalCount = isStaticTab
    ? staticTabCount
    : isCustomParticipantList
    ? finalCustomTotalCount
    : feedTotalCount;

  return {
    isLoading:
      !isStaticTab &&
      !isCustomParticipantList &&
      isLoading &&
      _.isEmpty(listData[activeTab]),
    isFetchingMore: isCustomParticipantList ? false : isFetchingMore,
    data: { listData, totalCount },
    hasNextPage: isCustomParticipantList ? false : hasNextPage,
    loadMore,
    hasGrades: isCustomParticipantList ? false : gradeIds.length > 0,
    hasCourses: isCustomParticipantList ? false : courseIds.length > 0,
    gradeIds,
    courseIds,
    gradeDetailsMap: isLazyLoadEnabled
      ? gradeDetailsMap
      : gradeDetailsMapFromEntities,
    courseDetailsMap: isLazyLoadEnabled
      ? courseDetailsMap
      : courseDetailsMapFromEntities,
    gradeSearchResults: isLazyLoadEnabled
      ? gradeSearchResults
      : gradeLocalSearchResults,
    courseSearchResults: isLazyLoadEnabled
      ? courseSearchResults
      : courseLocalSearchResults,
    isSearchLoading,
    isGradeSearchLoading: isLazyLoadEnabled && isGradeSearchLoading,
    isCourseSearchLoading: isLazyLoadEnabled && isCourseSearchLoading,
    isGradeCountsLoading:
      !isLazyLoadEnabled && isGradeCountsLoading && _.isEmpty(gradeCountsData),
    isCourseCountsLoading:
      !isLazyLoadEnabled &&
      isCourseCountsLoading &&
      _.isEmpty(courseCountsData),
    onGradeRangeChanged,
    onCourseRangeChanged,
    customDetailsMap: finalCustomDetailsMap,
    filteredActiveTabIds,
    customTotalCount: finalCustomTotalCount,
    isCustomLoading,
    onCustomRangeChanged: finalOnCustomRangeChanged,
  };
};

export default useParticipantsData;
